<template>
  <div>
    <div v-if="administratorDataIsLoading" class="d-flex justify-content-center m-5">
      <div class="spinner-border text-primary">
        <span class="sr-only">{{$t('state.loading')}}</span>
      </div>
    </div>
    <b-card v-if="!administratorDataIsLoading">
      <b-card-body>
        <div class="headline d-flex flex-row align-items-center w-100 ml-1 pb-3">
          <h2 class="card-title font-weight-bolder">{{ $t('administrator.details.security-roles.title') }}</h2>
        </div>
        <div class="col-12">
          <div class="row">
            <div v-required-role="'ROLE_BACKOFFICE_GROUP_ROLE_GET'" id="availableRoles" class="col-5">
              <b-card
                  border-variant="primary"
                  header-bg-variant="success"
                  header-text-variant="white"
                  align="left"
              >
                <template #header>
                  <div class="d-flex align-items-center justify-content-between">
                    <p class="mb-0">{{ availableRolesHeader }}</p>
                    <button class="btn btn-primary btn-xs"
                            @click="toggleAll('availableRoles')">{{ $t('administrator.edit-role.toggle-all') }}</button>
                  </div>
                </template>
                <div class="d-flex">
                  <b-form-group
                      label-for="group-key"
                      class="mr-1"
                  >
                    <b-form-input id="group-nam1e" :placeholder="$t('administrator.edit-role.find-role')" v-model="filterGroupsPhrase" trim></b-form-input>
                  </b-form-group>
                  <select v-model="filterCategorySelect" class="form-control">
                    <option>{{ allRolesFilterName }}</option>
                    <option>{{ noCategoryRoleFilterName }}</option>
                    <option :value="category" v-for="(category, index) in roleCategories" :key="index">{{ category }}</option>
                  </select>
                </div>
                <draggable v-required-role="'ROLE_BACKOFFICE_GROUP_ROLE_GET'" class="create-group-modal__groups-list" v-model="filteredRoles" group="people">
                  <div
                      v-for="role in filteredRoles"
                      :key="role.role"
                  >
                    <b-form-checkbox
                        :id="role.role"
                        :name="role.role"
                        :value="role.role"
                    >
                      <div class="d-flex create-group-modal__role-text" :title="role.role">
                        <div v-if="role.category" class="create-group-modal__role-text text-success font-weight-bold mr-1">{{ role.category }}</div>
                        <div class="font-weight-normal">{{ role.role }}</div>
                      </div>
                    </b-form-checkbox>
                  </div>
                </draggable>
              </b-card>
            </div>

            <div  class="col-2 d-flex flex-column justify-content-center align-items-center">
              <b-button @click="moveToSelectedRoles" class="w-100 mb-2 create-group-modal__operation-button" variant="success">
                {{ $t('administrator.edit-role.btn.add') }}
                <i class="fas fa-arrow-right ml-1"></i>
              </b-button>
              <b-button @click="moveToAvailableRoles" class="w-100 create-group-modal__operation-button" variant="danger">
                <i class="fas fa-arrow-left mr-1"></i>
                {{ $t('administrator.edit-role.btn.remove') }}
              </b-button>
            </div>

            <div v-required-role="'ROLE_BACKOFFICE_GROUP_ROLE_POST'" id="selectedRoles" class="col-5">
              <b-card
                  border-variant="primary"
                  header-bg-variant="success"
                  header-text-variant="white"
                  align="left"
              >
                <template #header>
                  <div class="d-flex align-items-center justify-content-between">
                    <p class="mb-0">{{ selectedRolesHeader }}</p>
                    <button class="btn btn-primary btn-xs" @click="toggleAll('selectedRoles')">{{ $t('administrator.edit-role.toggle-all') }}</button>
                  </div>
                </template>
                <div class="d-flex">
                  <b-form-group
                      label-for="group-key"
                      class="mr-1"
                  >
                    <b-form-input id="group-nam1e" :placeholder="$t('administrator.edit-role.find-group')" v-model="filterSelectedGroupsPhrase" trim></b-form-input>
                  </b-form-group>
                  <select v-model="filterSelectedCategorySelect" class="form-control">
                    <option>{{ allRolesFilterName }}</option>
                    <option>{{ noCategoryRoleFilterName }}</option>
                    <option :value="category" v-for="(category, index) in roleCategories" :key="index">{{ category }}</option>
                  </select>
                </div>
                <draggable class="create-group-modal__groups-list" v-model="filteredSelectedRoles" group="people">
                  <div
                      v-for="role in filteredSelectedRoles"
                      :key="role.role"
                  >
                    <b-form-checkbox
                        :id="role.role"
                        :name="role.role"
                        :value="role.role"
                    >
                      <div class="d-flex create-group-modal__role-text" :title="role.role">
                        <div v-if="role.category" class="create-group-modal__role-text text-success font-weight-bold mr-1">{{ role.category }}</div>
                        <div class="font-weight-normal">{{ role.role }}</div>
                      </div>
                    </b-form-checkbox>
                  </div>
                </draggable>
              </b-card>
            </div>
          </div>
        </div>
      </b-card-body>
    </b-card>
  </div>
</template>

<script>
import { mapState } from "vuex";
import draggable from 'vuedraggable';

export default {
  components: { draggable },
  data() {
    return {
      filterGroupsPhrase: '',
      filterCategorySelect: '',
      filterSelectedGroupsPhrase: '',
      filterSelectedCategorySelect: '',
      availableRolesChecked: [],
      selectedRolesChecked: [],
      roleCategories: [],
      allRolesFilterName: this.$t('administrator.roles-filter.all-role-filter'),
      noCategoryRoleFilterName: this.$t('administrator.roles-filter.no-category-role-filter')
    }
  },
  mounted() {
    if(this.roles) {
      this.setRoles();
    }
  },
  computed: {
    ...mapState({
      administratorDataIsLoading: state => state.administrator.administratorDataIsLoading,
      administratorData: state => state.administrator.administratorDetails,
      roles: state => state.administrator.roles,
      storeAvailableRoles: state => state.administrator.availableRoles,
      storeSelectedRoles: state => state.administrator.selectedRoles
    }),
    filteredRoles: {
      get() {
        let allRoles = this.$store.state.administrator.availableRoles;

        if(allRoles.length > 0 && this.filterCategorySelect !== '') {
          allRoles = allRoles.filter(role => {
            if (role.category && this.filterCategorySelect !== this.noCategoryRoleFilterName && this.filterCategorySelect !== this.allRolesFilterName) {
              return role.category.toLowerCase().match(this.filterCategorySelect.toLowerCase());
            } else if(!role.category && this.filterCategorySelect === this.noCategoryRoleFilterName) {
              return role;
            } else if(this.filterCategorySelect === this.allRolesFilterName) {
              return role;
            }
          });
        }

        const filteredRoles = allRoles.filter(role => role.role.toLowerCase().match(this.filterGroupsPhrase.toLowerCase()));
        const sortedRoles = this.sortRoles(filteredRoles);

        return sortedRoles;
      },
      set(value) {
        this.$store.commit('administrator/setAvailableRoles', value);
      }
    },
    filteredSelectedRoles: {
      get() {
        let allRoles = this.$store.state.administrator.selectedRoles;

        if(allRoles.length > 0 && this.filterSelectedCategorySelect !== '') {
          allRoles = allRoles.filter(role => {
            if (role.category && this.filterSelectedCategorySelect !== this.noCategoryRoleFilterName && this.filterSelectedCategorySelect !== this.allRolesFilterName) {
              return role.category.toLowerCase().match(this.filterSelectedCategorySelect.toLowerCase());
            } else if(!role.category && this.filterSelectedCategorySelect === this.noCategoryRoleFilterName) {
              return role;
            } else if(this.filterSelectedCategorySelect === this.allRolesFilterName) {
              return role;
            }
          });
        }

        const filteredRoles = allRoles.filter(role => role.role.toLowerCase().match(this.filterSelectedGroupsPhrase.toLowerCase()));
        const sortedRoles = this.sortRoles(filteredRoles);

        return sortedRoles;
      },
      set(value) {
        this.$store.commit('administrator/setSelectedRoles', value);
      }
    },
    availableRolesHeader() {
      const header = `${this.$t('administrator.edit-role.available_roles')} (${this.storeAvailableRoles.length})`;
      return header;
    },
    selectedRolesHeader() {
      const header = `${this.$t('administrator.edit-role.selected_roles')} (${this.storeSelectedRoles.length})`;
      return header;
    }
  },
  methods: {
    sortRoles(array) {
      function compare(a ,b) {
        if (a.category === b.category) {
          return 0;
        } else if (!a.category) {
          return 1;
        } else if (!b.category) {
          return -1;
        } else {
          return a.category > b.category ? 1 : -1;
        }
      }
      return array.sort(compare);
    },
    setRoles() {
      this.categories = this.prepareCategoryList(this.roles);

      if(this.administratorData) {
        const roles = [];

        this.roles.forEach(role => {
          this.administratorData.roles.forEach(administratorRole => {
            if(role.role === administratorRole) {
              roles.push(role);
            }
          })
        });

        const availableRoles = this.roles.filter(item => !roles.includes(item));
        this.$store.commit('administrator/setAvailableRoles', availableRoles);

        this.selectedRoles = roles;
        this.$store.commit('administrator/setSelectedRoles', roles);
      } else {
        this.$store.commit('administrator/setAvailableRoles', this.roles);
      }
    },
    moveToSelectedRoles() {
      const checkedCheckboxes = document.querySelectorAll('#availableRoles input:checked');

      checkedCheckboxes.forEach(checkbox => {
        const findCheckedRoles = this.storeAvailableRoles.find(role => role.role === checkbox.value);
        const selectedRoles = [...this.storeSelectedRoles];
        const availableRoles = this.storeAvailableRoles.filter(role => role.role !== checkbox.value);

        selectedRoles.push(findCheckedRoles);

        this.$store.commit('administrator/setSelectedRoles', selectedRoles);
        this.$store.commit('administrator/setAvailableRoles', availableRoles);
      });
    },
    moveToAvailableRoles() {
      const checkedCheckboxes = document.querySelectorAll('#selectedRoles input:checked');
      checkedCheckboxes.forEach(checkbox => {
        const findCheckedRoles = this.storeSelectedRoles.find(role => role.role === checkbox.value);

        const availableRoles = [...this.storeAvailableRoles];
        availableRoles.unshift(findCheckedRoles);

        const selectedRoles = this.storeSelectedRoles.filter(role => role.role !== checkbox.value);

        this.$store.commit('administrator/setSelectedRoles', selectedRoles);
        this.$store.commit('administrator/setAvailableRoles', availableRoles);
      });
    },
    toggleAll(id) {
      const checkboxes = document.querySelectorAll(`#${id} input`);

      checkboxes.forEach(checkbox => {
        checkbox.checked = !checkbox.checked;
      });
    },
    prepareCategoryList() {
      const noCategoriesLabel = this.$t('groups.add_new_group.no_categories');

      this.$store.dispatch('administrator/getRoleCategories')
          .then(data => {
            this.roleCategories = [noCategoriesLabel, ...data]
          })
    }
  },
  watch: {
    roles() {
      this.setRoles();
    },
    administratorData() {
      this.setRoles();
    }
  }
}
</script>
